/**
 * Bundle bars on the product page.
 *
 * - Hides the block while a subscription selling plan is selected in the buy buttons
 *   (Seal Subscriptions widget or the theme's native selling plan radios).
 * - Updates the portions line for the selected bar.
 * - When a bar other than the current product is selected, the add-to-cart button
 *   adds that product on its own instead of submitting the page form.
 * - When the page product is in the list (data-hides-one-time-price), Seal's
 *   one-time price is hidden while this block is visible.
 */
if (!customElements.get('bundle-products')) {
  customElements.define(
    'bundle-products',
    class BundleProducts extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section') || document;
        this.productRoot = this.section.querySelector('[id^="Product--"]') || this.section;
        this.portions = this.querySelector('[data-bundle-portions]');
        this.adding = false;

        this.onChange = (event) => {
          if (this.contains(event.target)) {
            this.updatePortions();
          } else {
            this.syncPlan();
          }
        };

        this.onClick = (event) => {
          const button = event.target.closest?.('[data-add-to-cart]');
          if (!button || this.hidden || !this.form?.contains(button)) return;

          const selected = this.selected;
          if (!selected || selected.hasAttribute('data-bundle-current')) return;

          event.preventDefault();
          event.stopPropagation();
          this.addSolo(selected.value, button);
        };

        this.observer = new MutationObserver(() => this.syncPlan());
        this.observer.observe(this.section.querySelector('.product__block--buttons') || this.section, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['class', 'aria-checked'],
        });

        this.section.addEventListener('change', this.onChange);
        document.addEventListener('click', this.onClick, true);

        this.syncPlan();
        this.updatePortions();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.productRoot?.classList.remove('bundle-hides-one-time-price');
        this.section.removeEventListener('change', this.onChange);
        document.removeEventListener('click', this.onClick, true);
      }

      get form() {
        return this.section.querySelector('[data-product-form]');
      }

      get selected() {
        return this.querySelector('input[type="radio"]:checked');
      }

      isSubscriptionSelected() {
        const active = this.section.querySelector(
          '.sls-option-container.sls-active, .sealsubs-container [role="radio"][aria-checked="true"]'
        );
        if (active) return !active.querySelector('input[data-type="one_time"]');

        const oneTime = this.section.querySelector('input[data-type="one_time"], [name="selling-plan-group"][value=""]');
        if (oneTime) return !oneTime.checked;

        return Boolean(this.section.querySelector('input[name="selling_plan"]')?.value);
      }

      syncPlan() {
        this.hidden = this.isSubscriptionSelected();
        this.syncOneTimePrice();
      }

      syncOneTimePrice() {
        const hide = this.hasAttribute('data-hides-one-time-price') && !this.hidden;
        this.productRoot.classList.toggle('bundle-hides-one-time-price', hide);
      }

      updatePortions() {
        if (!this.portions) return;

        const text = this.selected?.dataset.portionsText || '';
        this.portions.textContent = text;
        this.portions.hidden = !text;
      }

      async addSolo(variantId, button) {
        if (!variantId || this.adding || button.disabled) return;

        this.adding = true;
        button.disabled = true;
        button.classList.add('is-loading');
        document.querySelector('cart-drawer')?.classList.add('is-loading');

        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', this.form?.querySelector('[name="quantity"]')?.value || '1');

        const routes = window.theme?.routes || {};
        let response;

        try {
          response = await fetch(routes.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/javascript',
            },
            body,
          }).then((result) => result.json());
        } catch (error) {
          response = { status: 500, description: error?.message || '' };
        }

        this.adding = false;

        if (response.status) {
          this.finishAdd(button, true);
          this.showError(response.description || response.message || '');
          this.dispatchAdd('theme:product:add-error', response, button);
          return;
        }

        this.dispatchAdd('theme:product:add', response, button);

        if (window.theme?.settings?.cartType === 'page') {
          window.location = routes.cart_url || '/cart';
          return;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        this.finishAdd(button, false);
      }

      finishAdd(button, failed) {
        button.classList.remove('is-loading');
        if (failed) {
          button.disabled = false;
          document.querySelector('cart-drawer')?.classList.remove('is-loading');
        }
      }

      dispatchAdd(type, response, button) {
        button.dispatchEvent(new CustomEvent(type, { detail: { response, button }, bubbles: true }));
      }

      showError(message) {
        const holder = this.form?.querySelector('[data-cart-errors-container]');
        if (!holder) return;

        const wrap = document.createElement('div');
        wrap.className = 'errors';
        wrap.textContent = message;
        holder.replaceChildren(wrap);
      }
    }
  );
}
